import { Box, Flex, HStack, Modal as NBModal, Text } from 'native-base';
import { forwardRef } from 'react';
import { ViewStyle } from 'react-native';
import Button from 'ui/components/Button';
import Icon from 'ui/components/Icon';

import useModalController from './controller';
import { styles } from './styles';
import { ModalProps } from './types';

// NB Modal was not working with <React.StrictMode>
const Modal = forwardRef(function Modal(props: ModalProps, ref) {
  const {
    selectedItemId,
    children,
    headerText,
    headerTextColor,
    headerFontSize,
    leftBtnAction,
    leftBtnText,
    leftVariant,
    lefttBtnBgColor,
    leftBtnProps,
    leftBtnTextColor,
    rightBtnAction,
    rightBtnText,
    rightVariant,
    rightBtnBgColor,
    rightBtnProps,
    rightBtnTextColor,
    rightBtnWidth,
    btnFontWeight,
    btnFontSize,
    modalContentMaxW,
    modalContentMaxH,
    modalContentW,
    modalContentH,
    modalBgColor,
    modalFooterBgColor,
    showModal,
    handleClose,
    modalRef,
    showGroupButton,
    hideFooter,
    placement,
    rightBtnLoading,
    hideCloseBtn,
    bodyPadding = '16px',
    headerIconName,
    isHeaderIconCustom,
    modalContentProps,
    topOffset = 0,
    modalFooterPadding = 4,
    ...restModalProps
  } = useModalController(props, ref);

  const RightButton = () => (
    <Button
      onPress={() => !!rightBtnAction && rightBtnAction(selectedItemId)}
      variant={rightVariant}
      bgColor={rightBtnBgColor}
      isLoading={rightBtnLoading}
      {...rightBtnProps}
      w="100%"
      rounded={12}
    >
      <Text
        color={rightBtnTextColor}
        fontWeight={btnFontWeight}
        fontSize={{ base: 'sm', lg: 'md' }}
      >
        {rightBtnText}
      </Text>
    </Button>
  );

  const GroupButtons = () => (
    <Box w="full">
      <Flex
        justifyContent={'center'}
        flexDir={{ base: 'column-reverse', md: 'row' }}
        style={{ gap: 8 }}
      >
        <Box w={{ base: '100%', md: '1/2' }}>
          <Button
            onPress={leftBtnAction}
            variant={leftVariant}
            bgColor={lefttBtnBgColor}
            {...leftBtnProps}
          >
            <Text color={leftBtnTextColor} fontSize={{ base: 'sm', lg: 'md' }}>
              {leftBtnText}
            </Text>
          </Button>
        </Box>
        <Box w={{ base: '100%', md: '1/2' }}>
          <RightButton />
        </Box>
      </Flex>
    </Box>
  );

  const Footer = () => (
    <NBModal.Footer
      backgroundColor={modalFooterBgColor}
      borderTopColor={'transparent'}
      padding={modalFooterPadding}
      minH="72px"
    >
      {showGroupButton ? (
        <GroupButtons />
      ) : (
        <HStack w={rightBtnWidth}>
          <RightButton />
        </HStack>
      )}
    </NBModal.Footer>
  );

  return (
    <NBModal
      isOpen={showModal}
      onClose={handleClose}
      ref={modalRef}
      maxH="100vh"
      height="100%"
      position="sticky"
      top={topOffset}
      // Position "fixed" doesn't exit in the interface but is supported and
      // fixes the centering problem of the modal on Chrome.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      _overlay={{ style: { position: 'fixed' } as any }}
      {...restModalProps}
    >
      <NBModal.Content
        maxW={modalContentMaxW}
        maxH={modalContentMaxH}
        w={modalContentW}
        h={modalContentH}
        bgColor={modalBgColor}
        {...styles[placement]}
        {...modalContentProps}
      >
        {hideCloseBtn ? null : (
          <NBModal.CloseButton marginTop={'6px'} marginRight={'4px'} />
        )}
        {headerText ? (
          <>
            {headerIconName ? (
              <NBModal.Header bgColor={modalBgColor} flexShrink={0}>
                <HStack
                  w="full"
                  space={3}
                  alignItems="baseline"
                  color={headerTextColor}
                >
                  <Icon
                    icon={headerIconName}
                    color={headerTextColor}
                    size={headerFontSize as string}
                    isCustom={isHeaderIconCustom}
                  />
                  <Text
                    color={headerTextColor}
                    fontWeight={btnFontWeight}
                    fontSize={headerFontSize}
                  >
                    {headerText}
                  </Text>
                </HStack>
              </NBModal.Header>
            ) : (
              <NBModal.Header
                bgColor={modalBgColor}
                borderBottomColor={{ base: 'transparent', lg: 'gray.200' }}
                flexShrink={0}
              >
                <Text
                  color={headerTextColor}
                  fontWeight={btnFontWeight}
                  fontSize={headerFontSize}
                  width={{ base: '70%', md: '90%' }}
                >
                  {headerText}
                </Text>
              </NBModal.Header>
            )}
          </>
        ) : null}
        <NBModal.Body padding={bodyPadding} pt={0}>
          {children}
        </NBModal.Body>
        {hideFooter ? null : <Footer />}
      </NBModal.Content>
    </NBModal>
  );
});

export default Modal;
